import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/cropImage';

// ✅ 1. ĐỊNH NGHĨA ẢNH BÌA MẶC ĐỊNH (Office Background)
const DEFAULT_COVER = "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop";

export default function BusinessProfilePage() {
  const [profile, setProfile] = useState({
    _id: '',
    companyName: '', // Tên công ty (TechCorp)
    fullName: '',    // Tên người quản lý (Nguyen Gia Bao)
    email: '',
    phone: '',
    website: '',
    address: '',
    description: '',
    industry: 'Công nghệ thông tin',
    size: '10-50 nhân viên',
    avatar: '',     
    coverImage: '',
    followersCount: 0
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // State Crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(null);
  const [cropType, setCropType] = useState(null); 

  const fileInputAvatarRef = useRef(null);
  const fileInputCoverRef = useRef(null);
  // Ref để focus vào ô nhập tên công ty khi bấm icon edit
  const companyNameInputRef = useRef(null); 

  const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // --- 1. FETCH DATA ---
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('skillmatch_token');
        const res = await fetch(`${API_URL}/api/users/profile`, {
          headers: { 'x-auth-token': token }
        });
        if (res.ok) {
          const data = await res.json();
          setProfile({
            _id: data._id || data.user?._id,
            // Ưu tiên lấy companyName trong profile, nếu không có thì lấy tên user làm tạm
            companyName: data.profile?.companyName || data.companyName || "Tên Doanh Nghiệp",
            fullName: data.profile?.fullName || data.fullName, 
            email: data.email || '',
            phone: data.profile?.phone || data.profile?.phoneNumber || '',
            website: data.profile?.website || '',
            address: data.profile?.location || data.profile?.address || '', 
            description: data.profile?.bio || data.profile?.description || '',
            industry: data.profile?.industry || 'Công nghệ thông tin',
            size: data.profile?.size || '10-50 nhân viên',
            avatar: data.profile?.avatar || '',
            coverImage: data.profile?.coverImage || '',
            followersCount: data.followersCount || 0
          });
        }
      } catch (error) {
        console.error("Lỗi tải profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [API_URL]);

  // ... (Giữ nguyên các hàm xử lý ảnh: onFileChange, onCropComplete, handleCropSave)
  const onFileChange = (e, type) => { if (e.target.files && e.target.files.length > 0) { const file = e.target.files[0]; const reader = new FileReader(); reader.addEventListener('load', () => { setCurrentImageSrc(reader.result); setCropType(type); setCropModalOpen(true); }); reader.readAsDataURL(file); } };
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => { setCroppedAreaPixels(croppedAreaPixels); }, []);
  const handleCropSave = async () => { try { setSaving(true); const croppedImageBlob = await getCroppedImg(currentImageSrc, croppedAreaPixels); const formData = new FormData(); formData.append('avatar', croppedImageBlob, 'image.jpg'); const token = localStorage.getItem('skillmatch_token'); const uploadRes = await fetch(`${API_URL}/api/users/upload-avatar`, { method: 'POST', headers: { 'x-auth-token': token }, body: formData, }); if (!uploadRes.ok) throw new Error("Lỗi upload ảnh"); const uploadData = await uploadRes.json(); const imageUrl = uploadData.url; if (cropType === 'avatar') { setProfile(prev => ({ ...prev, avatar: imageUrl })); } else { setProfile(prev => ({ ...prev, coverImage: imageUrl })); } setCropModalOpen(false); setToast({ show: true, message: 'Đã tải ảnh lên! Hãy bấm "Lưu thay đổi" để hoàn tất.', type: 'success' }); } catch (error) { console.error(error); setToast({ show: true, message: 'Lỗi xử lý ảnh: ' + error.message, type: 'error' }); } finally { setSaving(false); setZoom(1); } };

  // --- 3. SUBMIT FORM ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('skillmatch_token');
      const res = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'x-auth-token': token 
        },
        body: JSON.stringify({
          companyName: profile.companyName,
          phone: profile.phone,
          website: profile.website,
          location: profile.address, 
          address: profile.address,
          description: profile.description, 
          bio: profile.description,
          industry: profile.industry,
          size: profile.size,
          avatar: profile.avatar,
          coverImage: profile.coverImage
        })
      });

      if (res.ok) {
        setToast({ show: true, message: 'Đã lưu thông tin doanh nghiệp!', type: 'success' });
      } else {
        throw new Error('Lỗi cập nhật');
      }
    } catch (error) {
      setToast({ show: true, message: 'Có lỗi xảy ra.', type: 'error' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast({ ...toast, show: false }), 3000);
    }
  };

  const getMapUrl = (address) => {
    const query = encodeURIComponent(address || "Vietnam");
    return `https://maps.google.com/maps?q=${query}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  };

  if (loading) return <div className="flex h-96 items-center justify-center text-gray-500">Đang tải hồ sơ...</div>;

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-sans pb-20">
      
      {/* HEADER */}
      <div className="sticky top-[70px] z-10 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shadow-sm transition-all">
        <h1 className="text-xl font-bold text-gray-900">Hồ sơ Doanh nghiệp</h1>
        <div className="flex gap-3">
            
            <button 
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md disabled:opacity-70"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span className="material-symbols-outlined text-[18px]">save</span>}
              Lưu thay đổi
            </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 space-y-6">
        
        {/* --- BRANDING --- */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
            {/* Cover Image: Sử dụng DEFAULT_COVER nếu không có ảnh */}
            <div className="relative h-48 md:h-64 bg-gray-200">
                <img 
                    src={profile.coverImage || DEFAULT_COVER} 
                    alt="Cover" 
                    className="w-full h-full object-cover" 
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>

                
            </div>

            {/* Avatar & Info */}
            <div className="px-6 pb-6 relative flex flex-col md:flex-row items-start md:items-end -mt-12 md:-mt-16 gap-6">
                
                {/* Avatar */}
                <div className="relative z-10">
                    <div className="w-32 h-32 rounded-full border-[4px] border-white shadow-md bg-white overflow-hidden flex-shrink-0 relative group/avatar cursor-pointer">
                        {profile.avatar ? (
                            <img src={profile.avatar} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 font-bold text-4xl">
                                {profile.companyName.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div 
                            onClick={() => fileInputAvatarRef.current.click()}
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity rounded-full"
                        >
                            <span className="material-symbols-outlined text-white text-3xl">photo_camera</span>
                        </div>
                        <input type="file" ref={fileInputAvatarRef} className="hidden" accept="image/*" onChange={(e) => onFileChange(e, 'avatar')} />
                    </div>
                </div>

                <div className="flex-1 w-full md:w-auto pt-2 md:pt-0">
                    <div className="mb-1">
                        <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1 block">Tên doanh nghiệp hiển thị</label>
                        
                        {/* ✅ FIX: INPUT TÊN CÔNG TY CÓ ICON EDIT */}
                        <div className="relative group/editname flex items-center max-w-lg">
                            <input 
                                ref={companyNameInputRef}
                                type="text" 
                                name="companyName"
                                value={profile.companyName} 
                                onChange={handleChange}
                                className="block w-full text-2xl md:text-3xl font-bold text-gray-900 border border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg px-2 -ml-2 transition-all bg-transparent focus:bg-white"
                                placeholder="Nhập tên công ty..."
                            />
                            <button 
                                onClick={() => companyNameInputRef.current.focus()}
                                className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full opacity-0 group-hover/editname:opacity-100 transition-all"
                                title="Chỉnh sửa tên"
                            >
                                <span className="material-symbols-outlined text-[20px]">edit</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 items-center">
                        <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[18px]">category</span> {profile.industry}</span>
                        <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[18px]">groups</span> {profile.size}</span>
                        <span className="flex items-center gap-1 text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                            <span className="material-symbols-outlined text-[16px]">person_add</span> 
                            {profile.followersCount} người theo dõi
                        </span>
                        {profile.website && (
                            <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline ml-2">
                                <span className="material-symbols-outlined text-[18px]">language</span> Website
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>

        {/* ... (Các phần Content Grid, Sidebar, Modal giữ nguyên như cũ) */}
        {/* Chỉ hiển thị phần thay đổi quan trọng ở trên, phần dưới giống file cũ */}
        
        {/* --- Copy lại phần Grid Layout bên dưới từ file cũ --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600">description</span> Giới thiệu về công ty
                    </h3>
                    <textarea 
                        name="description" 
                        rows="8"
                        value={profile.description} onChange={handleChange}
                        placeholder="Mô tả về văn hóa, sứ mệnh, môi trường làm việc..."
                        className="w-full p-4 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y leading-relaxed" 
                    ></textarea>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined text-red-500">location_on</span> Địa điểm làm việc
                    </h3>
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Địa chỉ trụ sở chính</label>
                        <input 
                            type="text" name="address" 
                            value={profile.address} onChange={handleChange}
                            placeholder="Nhập địa chỉ đầy đủ (VD: Số 1 Đại Cồ Việt, Hà Nội)..."
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                        />
                    </div>
                    <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 relative">
                        {profile.address ? (
                            <iframe 
                                title="Company Location"
                                width="100%" 
                                height="100%" 
                                frameBorder="0" 
                                scrolling="no" 
                                src={getMapUrl(profile.address)}
                                className="filter grayscale hover:grayscale-0 transition-all duration-500"
                            ></iframe>
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                                Nhập địa chỉ để hiển thị bản đồ
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-5">
                    <h3 className="font-bold text-gray-900 border-b border-gray-100 pb-3">Chi tiết</h3>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ngành nghề</label>
                        <input type="text" name="industry" value={profile.industry} onChange={handleChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Quy mô</label>
                        <select name="size" value={profile.size} onChange={handleChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none bg-white focus:border-blue-500">
                            <option>1-10 nhân viên</option>
                            <option>10-50 nhân viên</option>
                            <option>50-200 nhân viên</option>
                            <option>200-500 nhân viên</option>
                            <option>500+ nhân viên</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Website</label>
                        <input type="text" name="website" value={profile.website} onChange={handleChange} placeholder="https://..." className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-5">
                    <h3 className="font-bold text-gray-900 border-b border-gray-100 pb-3">Liên hệ (Riêng tư)</h3>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email liên hệ</label>
                        <input type="text" value={profile.email} disabled className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 cursor-not-allowed" />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Số điện thoại</label>
                        <input type="text" name="phone" value={profile.phone} onChange={handleChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* --- CROP MODAL & TOAST (Giữ nguyên) --- */}
      {cropModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4">
          <div className="bg-white rounded-xl overflow-hidden w-full max-w-2xl flex flex-col h-[80vh] animate-fadeIn">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-gray-800">Cắt ảnh {cropType === 'avatar' ? 'Logo' : 'Bìa'}</h3>
              <button onClick={() => setCropModalOpen(false)} className="text-gray-500 hover:text-gray-700"><span className="material-symbols-outlined">close</span></button>
            </div>
            
            <div className="relative flex-1 bg-black">
              <Cropper image={currentImageSrc} crop={crop} zoom={zoom} aspect={cropType === 'avatar' ? 1 : 3 / 1} cropShape={cropType === 'avatar' ? 'round' : 'rect'} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
            </div>

            <div className="p-4 bg-white border-t border-gray-100 flex items-center gap-4">
              <div className="flex-1"><label className="text-xs font-semibold text-gray-500 mb-1 block">Zoom</label><input type="range" value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" /></div>
              <button onClick={handleCropSave} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-lg">{saving ? 'Đang xử lý...' : 'Cắt & Lưu'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {toast.show && createPortal(
        <div className={`fixed bottom-5 left-5 z-[9999] px-4 py-3 rounded-lg shadow-xl text-white flex items-center gap-3 animate-slideUp ${toast.type === 'error' ? 'bg-red-600' : 'bg-gray-800'}`}>
          <span className="material-symbols-outlined">{toast.type === 'error' ? 'error' : 'check_circle'}</span>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
      `}</style>
    </div>
  );
}